/**
 * ================================================================================================
 *  ביצוע חיובים — who is due, what each one would be charged, and the press that charges them
 * ================================================================================================
 *
 * The Owner, 2026-11-02:
 *
 *     *"אני צריך טבלה מרכזית של הלקוחות המיועדים לחיוב, פירוט החישוב לכל לקוח, בחירת לקוח או
 *      קבוצת לקוחות ואישור ביצוע החיוב, ולאחר מכן תוצאות לכל לקוח וקישור למסמך שהופק. החיוב הידני
 *      והמתוזמן חייבים להשתמש באותו מנגנון ולמנוע חיוב כפול."*
 *
 * ── THE TABLE IS THE REAL RUN, NOT A PREVIEW OF ONE ────────────────────────────────────────────
 *
 * `platform.billing.due.read` calls `BillingRunService.run` with `{ apply: false }` — the same
 * assembly, the same `priceCycle`, the same outcome words as the nightly timer. It writes no
 * document, claims no period and calls no provider, and it does not even record today's usage:
 * that is the daily job's, and a figure approved here must be the figure the daily series
 * produced.
 *
 * SO THE AMOUNT IN THE TABLE IS THE AMOUNT THAT WILL BE CHARGED, by construction rather than by
 * promise — and when it is not chargeable the row says why, in the run's own vocabulary.
 *
 * ── WHAT PREVENTS A DOUBLE CHARGE IS NOT THIS SCREEN ───────────────────────────────────────────
 *
 * It is the idempotency key, derived per (subscription, period, settled-failure count) and UNIQUE
 * in the database. The manual trigger and the timer compute the same key because they are one code
 * path, so whichever runs second is refused by the constraint. This screen can be pressed twice
 * and the second press reports `already_charged` or `blocked_in_flight` — which is what it is for.
 *
 * ── THE SELECTION IS EXPLICIT, AND AN EMPTY ONE CHARGES NOBODY ─────────────────────────────────
 *
 * Nothing is ticked when the table loads. There is no "charge everybody" default, because the
 * difference between "I chose all of them" and "I did not choose" is the difference between a
 * decision and an accident — and the route refuses an empty selection rather than treating it as
 * all.
 */

import { useCallback, useState } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { InfoNote } from '../components/InfoNote';
import { Modal } from '../components/Modal';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import {
  BillingRunTable,
  customersHe,
  dayMonthYear,
  SELECTABLE_OUTCOME,
} from '../components/BillingRunTable';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import type { RunLine, RunReport } from '../api/contract';
import { billingOutcomeHe } from '../api/billingText';
import { useServerData } from '../api/useServerData';
import { routes } from '../routes';

const TITLE_HE = 'ביצוע חיובים';

/**
 * WHICH OUTCOMES A PERSON MAY ACT ON.
 *
 * `would_charge` is the only line a press changes: everything else is already decided — skipped,
 * blocked or charged — and offering a tick beside it would invite an operator to select rows that
 * cannot move and then wonder why the results say nothing happened.
 *
 * IT IS NOT A HIDDEN ROW. The row is listed, with its outcome in words; what it has not got is a
 * checkbox.
 */
const SELECTABLE = 'would_charge';

type Outcome =
  | { readonly kind: 'done'; readonly report: RunReport }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function PlatformBillingRunScreen(): JSX.Element {
  const ctx = useWorkContext();
  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }
  return <PlatformBillingRunBody />;
}

function PlatformBillingRunBody(): JSX.Element {
  const read = useCallback(() => callOperation('platform.billing.due.read', {}), []);
  const dueRead = useServerData(read);

  /*
   * ── EVERY ROW THAT IS READY TO CHARGE IS TICKED WHEN THE TABLE ARRIVES. **2026-11-03.** ──────
   *
   * The Owner: *"סמן מראש רק שורות שמוכנות לחיוב, אפשר לי להחריג לקוחות."*
   *
   * Until today nothing was ticked and there was a *"select everything chargeable"* button, so the
   * ordinary month — charge everybody — cost a press before it could begin, and the exceptional
   * one looked identical to it.
   *
   * `null` MEANS *"THE OPERATOR HAS NOT CHOSEN YET"*, and is not the same as an empty selection.
   * It is resolved to every selectable row at render. The moment anything is ticked or unticked
   * the state becomes an explicit list — including the empty one, so an operator who clears the
   * table does not find it re-ticked on the next render.
   *
   * ONLY ROWS THAT CAN BE CHARGED ARE TICKED. A blocked office stays in the table with its
   * reason and a disabled box; pre-ticking it would offer an act that is refused.
   */
  const [picked, setPicked] = useState<readonly string[] | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const back = { labelHe: 'מרכז החיוב', href: routes.platformCharges };

  /*
   * THE FIRST TICK RESOLVES THE DEFAULT. `was` is `null` until the operator touches the table, so
   * the toggle has to know what was ON SCREEN in order to remove one row from it — otherwise the
   * first untick would produce a list of ONE instead of removing one from all.
   */
  function toggle(uid: string, defaults: readonly string[]): void {
    setPicked((was) => {
      const current = was ?? defaults;
      return current.includes(uid) ? current.filter((u) => u !== uid) : [...current, uid];
    });
  }

  /**
   * THE UIDS ARE PASSED IN AND NEVER READ FROM STATE HERE. **2026-11-03.**
   *
   * `picked` is `null` until the operator touches the table — it means *"the default, which is
   * every chargeable row"*. Reading it here would send an EMPTY list for the most ordinary case
   * of all: an operator who agrees with the recommendation and presses charge without ticking
   * anything. The run would answer success having charged nobody, which is the shape this
   * screen's own notes call *"a press that charged nobody and answered 200"*.
   *
   * So the caller passes exactly the rows it rendered as ticked.
   */
  async function execute(uids: readonly string[]): Promise<void> {
    setSending(true);
    try {
      /*
       * A WRITE, AND NEVER RETRIED. The client cannot know whether the server committed, and a
       * repeated run is a second claim on the same periods — refused by the key, and still a
       * second audit row against customers who were charged once.
       */
      const report = await callOperation('platform.billing.run.execute', {
        body: { subscriptionUids: [...uids], reason },
      });
      setOutcome({ kind: 'done', report });
      /* An explicit empty list: the run happened, and nothing should be re-ticked over it. */
      setPicked([]);
    } catch (err) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
      setConfirming(false);
      setReason('');
    }
  }

  return (
    <AppShell titleHe={TITLE_HE} backToParent={back}>
      <InfoNote summaryHe="מה המסך הזה עושה, ומה הוא אינו עושה">
        <p>
          הטבלה היא הריצה האמיתית במצב מדידה בלבד: אותו חישוב, אותן מילות תוצאה, בלי להפיק מסמך,
          בלי לתפוס תקופה ובלי לפנות לספק. הסכום שמוצג הוא הסכום שייגבה.
        </p>
        <p>
          מה שמונע חיוב כפול אינו המסך אלא מפתח האידמפוטנטיות, שהוא ייחודי בבסיס הנתונים. הריצה
          הידנית והמתוזמנת מחשבות אותו מפתח, ולכן השנייה מביניהן נדחית — לחיצה שנייה כאן תדווח
          „חויב כבר” ולא תגבה פעמיים.
        </p>
        <p>
          תקופה נגבית רק אחרי שהסתיימה. יום שבו המערכת לא מדדה שימוש חוסם את התקופה בשם, במקום
          לתמחר אותה על נתון חסר.
        </p>
        <p>
          לחיצה ידנית היא מעשה של אדם, ולכן היא דורשת סיבה. הריצה המתוזמנת גובה בלי סיבה
          מפני שאיש אינו מפעיל אותה.
        </p>
      </InfoNote>

      {outcome === null ? null : outcome.kind === 'failed' ? (
        <ActionFailureNotice
          failure={outcome.failure}
          /*
           * NOTHING IS RE-SENT. Clearing the refusal puts the operator back where they were, with
           * the selection they made, and they press again — *"a write that replays itself after an
           * authentication step is a write nobody pressed"*, and a replayed BILLING RUN is the one
           * write in this product that must never replay itself.
           */
          onStepUpSatisfied={() => setOutcome(null)}
        />
      ) : (
        <ResultsPanel report={outcome.report} />
      )}

      <ServerRead result={dueRead.result} whatHe="את הלקוחות המיועדים לחיוב" skeletonRows={6}>
        {(report) => {
          const selectable = report.lines.filter((l) => l.outcome === SELECTABLE);
          /* The default: everything that can be charged. See `picked`'s own note. */
          const defaults = selectable.map((l) => l.subscriptionUid);
          const effective = picked ?? defaults;
          const chosen = selectable.filter((l) => effective.includes(l.subscriptionUid));
          return (
            <>
              <BillingRunTable
                report={report}
                picked={effective}
                onToggle={(uid) => toggle(uid, defaults)}
                onPick={setPicked}
              />

              {/*
                ── EXACTLY WHO IS ABOUT TO BE CHARGED, ON THE SCREEN AND NOT IN THE DIALOG ──────
                *"לפני הביצוע הצג את כל הלקוחות שנבחרו בפועל, את תקופת החיוב ואת הסכום הכולל,
                ורק אז בקש אישור לחיוב."*

                A COUNT IS NOT A LIST. The dialog said *"two customers, 468.00"* and both numbers
                were right — an operator who filtered, ticked, filtered again and pressed had no
                way to check WHICH two.

                AND IT IS NOT IN THE MODAL, because 09 §2.1 M4 refuses one: *"a modal contains no
                table the user must read row by row. It is a child screen with an address, not a
                modal."* The control fired on the first attempt, and it is right — a list you have
                to read is a list you may want to CHANGE, and a modal is the one place you cannot.
                So the review is here, above the press, where unticking a row is one click away.
              */}
              {chosen.length === 0 ? null : (
                <details className="notice" data-area="confirm-list" open>
                  <summary>{`הלקוחות שייכללו בחיוב (${customersHe(chosen.length)})`}</summary>
                  <table className="table table--compact">
                    <thead>
                      <tr>
                        <th scope="col">לקוח</th>
                        <th scope="col">תקופה</th>
                        <th scope="col" className="is-end">סכום</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chosen.map((l) => (
                        <tr key={l.subscriptionUid}>
                          <td>
                            {l.tenantDisplayNo === null
                              ? l.tenantLegalName
                              : `${l.tenantDisplayNo} · ${l.tenantLegalName}`}
                          </td>
                          <td>
                            <span dir="ltr">{`${dayMonthYear(l.periodStart)} — ${dayMonthYear(l.periodEnd)}`}</span>
                          </td>
                          <td className="is-end">
                            <span dir="ltr">
                              {l.amountTotal === null ? '—' : `${l.amountTotal} ₪`}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}

              {/*
                WHAT IS SELECTED AND WHAT IT COMES TO, BESIDE THE PRESS. The Owner:
                *"כשאני מסמן לקוחות, הצג כמה לקוחות נבחרו ומה הסכום הכולל שאישרתי לחייב."*
                The button's own label says the count; this says the money, which is the figure a
                person checks against their own expectation before opening the dialog at all.
              */}
              <p className="notice__body" data-area="selection-summary">
                {chosen.length === 0
                  ? 'לא נבחר אף לקוח לחיוב.'
                  : `${chosen.length === 1 ? 'נבחר' : 'נבחרו'} ${customersHe(chosen.length)} · סך הכול ${totalOf(chosen)} ₪ כולל מע״מ.`}
              </p>

              <div className="actions-row">
                <button
                  type="button"
                  className="btn btn--primary"
                  /*
                   * A STABLE HOOK, BECAUSE THE WORD IS NO LONGER UNIQUE. **2026-11-03.** The table
                   * gained a *"מוכנים לחיוב"* quick filter and a *"סכום לחיוב"* column header, both
                   * buttons and both earlier in the document — so a suite finding the press by its
                   * text found a filter instead and reported that the press did nothing.
                   */
                  data-area="charge"
                  disabled={chosen.length === 0}
                  onClick={() => setConfirming(true)}
                >
                  {chosen.length === 0 ? 'חיוב הלקוחות שנבחרו' : `חיוב ${customersHe(chosen.length)}`}
                </button>
              </div>

              {!confirming ? null : (
                <Modal
                  intentHe="ביצוע חיוב"
                  /*
                   * 09 §2.2 — the blast radius NAMES WHAT WILL HAPPEN and what will not. The count
                   * and the total are read from the rows the operator actually ticked, not from
                   * the table: a figure computed from anything else would be a different promise
                   * from the one being approved.
                   */
                  blastRadiusHe={
                    `${customersHe(chosen.length)} ${chosen.length === 1 ? 'יחויב' : 'יחויבו'} עכשיו, ` +
                    `בסך הכול ${totalOf(chosen)} ₪ כולל מע״מ. ` +
                    'הכרטיס מחויב דרך הספק, מופק מסמך, והתקופה נתפסת כך שלא תיגבה שוב. ' +
                    'הפעולה אינה משנה תכנית, אינה משנה מכסה ואינה מבטלת מנוי.'
                  }
                  fields={1}
                  irreversible
                  confirmLabelHe={sending ? 'מחייב…' : 'חיוב עכשיו'}
                  onConfirm={() => {
                    if (reason.trim() !== '') void execute(chosen.map((l) => l.subscriptionUid));
                  }}
                  onClose={() => {
                    setConfirming(false);
                    setReason('');
                  }}
                >
                  <label className="field">
                    <span className="field__label">סיבת החיוב הידני</span>
                    <input
                      className="field__input"
                      name="billing-run-reason"
                      value={reason}
                      onChange={(e) => setReason(e.currentTarget.value)}
                    />
                    <span className="field__hint">הסיבה נרשמת ביומן יחד עם מי שביצע.</span>
                  </label>
                  {reason.trim() !== '' ? null : (
                    <p className="form__note">הפעולה אינה נשלחת עד שהוזנה סיבה.</p>
                  )}
                </Modal>
              )}
            </>
          );
        }}
      </ServerRead>
    </AppShell>
  );
}

/**
 * HOW MANY CUSTOMERS, IN HEBREW. **Seen in the browser, 2026-11-02.**
 *
 * The button said *"חיוב 1 לקוחות שנבחרו"* and the dialog said *"1 לקוחות יחויבו עכשיו"*. A dom
 * assertion of `toContain('1')` is true of that sentence and of the right one, which is why this
 * was found by looking rather than by the suite — the same reason the layout complaints were.
 *
 * ZERO IS NOT RENDERED BY EITHER CALLER: the button is disabled and the dialog cannot open. It is
 * handled anyway, because a function whose caller guarantees its input is a function that will be
 * called from somewhere else one day.
 */
/** The ticked rows' totals, summed as STRINGS are not summable — see the note inside. */
function totalOf(lines: readonly RunLine[]): string {
  /*
   * THE AMOUNTS ARE FIXED-POINT STRINGS AND THIS IS THE ONE PLACE THEY ARE ADDED.
   *
   * `money-sum.ts` on the server adds them in scaled integers precisely because a price that
   * passed through a JS number is a rounded price. This is a CONFIRMATION SENTENCE and not a
   * figure anything is charged from — the server adds nothing from it, and each line is charged on
   * its own recorded amount — so it is computed in agorot and formatted back, never in floating
   * point.
   */
  let agorot = 0n;
  for (const line of lines) {
    const raw = line.amountTotal;
    if (raw === null) continue;
    const [whole = '0', frac = ''] = raw.split('.');
    const cents = `${frac}00`.slice(0, 2);
    agorot += BigInt(whole) * 100n + BigInt(cents);
  }
  const sign = agorot < 0n ? '-' : '';
  const abs = agorot < 0n ? -agorot : agorot;
  return `${sign}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
}

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  `DuePanel` MOVED TO `BillingRunTable`. **2026-11-03.**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * It was a five-column table with a tick and a select-all. The Owner's specification turned it
 * into eleven columns, a sort and a typed filter on every header, five quick filters with counts,
 * a notice for selected rows a filter is hiding, and a per-row breakdown — which is a component
 * with state of its own and not a render helper inside a screen.
 *
 * THE SCREEN STILL OWNS THE SELECTION. The table reports a tick and never decides what is
 * charged; the press, the reason, the confirmation and the run stay here, where the capability is.
 */
function ResultsPanel({ report }: { readonly report: RunReport }): JSX.Element {
  return (
    <section className="notice" data-tone="info" data-area="results">
      <h2 className="notice__title">תוצאות החיוב</h2>
      <table>
        <thead>
          <tr>
            <th scope="col">לקוח</th>
            <th scope="col">תקופה</th>
            <th scope="col">סכום</th>
            <th scope="col">תוצאה</th>
            <th scope="col">המסמך</th>
          </tr>
        </thead>
        <tbody>
          {report.lines.map((line) => {
            const outcome = billingOutcomeHe(line.outcome);
            return (
              <tr key={`result-${line.subscriptionUid}`}>
                <td>
                  {/*
                    A `skipped_not_due` LINE HAS NO CUSTOMER. The run answers it for a uid the
                    operator selected that `due()` did not return — there is no tenant row behind
                    it, so `tenantUid` is empty, and `routes.platformTenant('')` THROWS by design:
                    09 §1.5, *"a URL carries only opaque uids, never a business value."*
                    One such row would have taken this panel to the fault boundary, on the screen
                    somebody is standing on immediately after charging people.
                  */}
                  {line.tenantUid === '' ? (
                    <span dir="ltr">{line.subscriptionUid}</span>
                  ) : (
                    <a href={routes.platformTenant(line.tenantUid)}>
                      {line.tenantLegalName === '' ? line.subscriptionUid : line.tenantLegalName}
                    </a>
                  )}
                </td>
                <td>
                  <span dir="ltr">{`${line.periodStart} — ${line.periodEnd}`}</span>
                </td>
                <td>
                  <span dir="ltr">{line.amountTotal ?? '—'}</span>
                </td>
                <td>
                  <StatusChip labelHe={outcome.labelHe} tone={outcome.tone} />
                  {line.detail === null ? null : (
                    /*
                     * `dir="ltr"` BECAUSE WHAT SURVIVES HERE IS NOT HEBREW AND IS NOT OURS.
                     * **2026-11-03.** The redundant English constants were removed from the
                     * server on the same day; what reaches this line now is a provider's own
                     * words, a reason code or a claim outcome -- technical text that renders
                     * backwards inside an RTL paragraph if it is not marked.
                     */
                    <p className="notice__body" dir="ltr">
                      {line.detail}
                    </p>
                  )}
                </td>
                <td>
                  {line.documentUid === null || line.tenantUid === '' ? (
                    /*
                     * NO DOCUMENT IS A STATE AND NOT A MISSING LINK. A blocked or skipped line
                     * raised none; a charged line whose provider returned no number raised one
                     * that is `provider_failed`, and the customer's own card shows that.
                     */
                    <span>—</span>
                  ) : (
                    <a href={`${routes.platformTenant(line.tenantUid)}#billing`}>
                      {/*
                        THE CUSTOMER'S CARD AND NOT THE DOCUMENT ITSELF. 08 §12B.1 gives the
                        platform *"view billing & subscription history"* and gives the DOWNLOAD to
                        the tenant — fetching somebody's tax document as an operator is a row the
                        master does not grant. The card's billing tab shows this document's state,
                        its number once the provider issues one, and its failure reason if it did
                        not.
                      */}
                      המסמך בכרטיס הלקוח
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
